package es.subastia.favorites

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import es.subastia.navigation.NavRoute
import es.subastia.navigation.Navigator
import es.subastia.ui.components.LoginRequiredPlaceholder
import es.subastia.ui.components.RichAuctionCard
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import subastia.composeapp.generated.resources.Res
import subastia.composeapp.generated.resources.desc_remove_favorite
import subastia.composeapp.generated.resources.empty_favorites_subtitle
import subastia.composeapp.generated.resources.empty_favorites_title
import subastia.composeapp.generated.resources.login_required_favorites
import subastia.composeapp.generated.resources.title_favorites

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun FavoritesScreen(navigator: Navigator) {
    val viewModel: FavoritesViewModel = koinViewModel()
    val state by viewModel.uiState.collectAsStateWithLifecycle()

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(stringResource(Res.string.title_favorites), fontWeight = FontWeight.Bold) },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = MaterialTheme.colorScheme.surface,
                    titleContentColor = MaterialTheme.colorScheme.onSurface,
                ),
            )
        },
    ) { padding ->
        Box(Modifier.padding(top = padding.calculateTopPadding()).fillMaxSize()) {
            when (val stateValue = state) {
                FavoritesUiState.Loading -> {
                    CircularProgressIndicator(Modifier.align(Alignment.Center))
                }
                FavoritesUiState.NotLoggedIn -> {
                    LoginRequiredPlaceholder(
                        message = stringResource(Res.string.login_required_favorites),
                        navigator = navigator,
                    )
                }
                is FavoritesUiState.Error -> {
                    Text(
                        stringResource(stateValue.error),
                        color = MaterialTheme.colorScheme.error,
                        modifier = Modifier.align(Alignment.Center),
                    )
                }
                is FavoritesUiState.Success -> {
                    if (stateValue.list.isEmpty()) {
                        Column(
                            modifier = Modifier.fillMaxSize(),
                            verticalArrangement = Arrangement.Center,
                            horizontalAlignment = Alignment.CenterHorizontally,
                        ) {
                            Text(stringResource(Res.string.empty_favorites_title), style = MaterialTheme.typography.titleMedium)
                            Spacer(Modifier.height(8.dp))
                            Text(
                                stringResource(Res.string.empty_favorites_subtitle),
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                            )
                        }
                    } else {
                        LazyVerticalGrid(
                            columns = GridCells.Adaptive(320.dp),
                            contentPadding = PaddingValues(16.dp),
                            verticalArrangement = Arrangement.spacedBy(16.dp),
                            horizontalArrangement = Arrangement.spacedBy(16.dp),
                            modifier = Modifier.fillMaxSize(),
                        ) {
                            items(stateValue.list, key = { it.auctionId }) { auction ->
                                RichAuctionCard(
                                    auction = auction,
                                    onClick = { navigator.navigateTo(NavRoute.AuctionDetail(auction.auctionId)) },
                                    trailingContent = {
                                        // Delete Button Overlaid on Image
                                        IconButton(
                                            onClick = { viewModel.removeFromFavorites(auction.auctionId) },
                                            modifier = Modifier.align(Alignment.BottomEnd).padding(8.dp),
                                            colors = IconButtonDefaults.iconButtonColors(
                                                containerColor = MaterialTheme.colorScheme.surface.copy(alpha = 0.7f),
                                                contentColor = Color(0xFF4CAF50),
                                            ),
                                        ) {
                                            Icon(Icons.Default.Delete, contentDescription = stringResource(Res.string.desc_remove_favorite))
                                        }
                                    },
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}
